import { supabase } from '../lib/supabase';
import { logger, generateRequestId } from '../utils/logger';

/**
 * Service for verifying subscription status after Stripe checkout
 * Polls database to confirm webhook has processed and subscription is active
 */

export const subscriptionVerificationService = {
  /**
   * Poll subscription status until premium plan is activated or timeout
   * @param {string} expectedPlanType - Expected plan type (premium, ultra, family)
   * @param {Object} options - Polling options
   * @returns {Promise<{verified: boolean, subscription: Object, error: string}>}
   */
  async pollSubscriptionStatus(expectedPlanType, options = {}) {
    const {
      maxAttempts = 20,        // Maximum polling attempts
      intervalMs = 2000,       // Interval between attempts (2 seconds)
      timeoutMs = 40000        // Total timeout (40 seconds)
    } = options;

    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    const startTime = Date.now();
    let attempts = 0;

    logger?.debug('Starting subscription verification polling', {
      requestId,
      expectedPlanType,
      maxAttempts,
      intervalMs
    });

    while (attempts < maxAttempts) {
      attempts++;

      // Check if timeout exceeded
      if (Date.now() - startTime > timeoutMs) {
        logger?.warn('Subscription verification timeout', {
          requestId,
          attempts,
          elapsedMs: Date.now() - startTime
        });
        return {
          verified: false,
          subscription: null,
          error: 'Verification timeout - subscription may still be processing'
        };
      }

      try {
        // Get current user
        const { data: { user } } = await supabase?.auth?.getUser();
        if (!user) {
          return {
            verified: false,
            subscription: null,
            error: 'User not authenticated'
          };
        }

        // Fetch subscription with plan details
        const { data: subscription, error: fetchError } = await supabase
          ?.from('user_subscriptions')
          ?.select(`
            *,
            plan:subscription_plans(*)
          `)
          ?.eq('user_id', user?.id)
          ?.single();

        if (fetchError) {
          logger?.error('Failed to fetch subscription during polling', fetchError, {
            requestId,
            attempt: attempts
          });
          
          // Wait before retry
          await this.sleep(intervalMs);
          continue;
        }

        // Check if subscription is active and matches expected plan
        const isActive = subscription?.status === 'active';
        const isPremiumPlan = subscription?.plan?.plan_type !== 'basic' && 
                             subscription?.plan?.plan_type !== 'trial';
        const matchesExpectedPlan = expectedPlanType ? 
          subscription?.plan?.plan_type === expectedPlanType : 
          isPremiumPlan;

        logger?.debug('Subscription verification attempt', {
          requestId,
          attempt: attempts,
          status: subscription?.status,
          planType: subscription?.plan?.plan_type,
          isActive,
          isPremiumPlan,
          matchesExpectedPlan
        });

        // Verification successful
        if (isActive && matchesExpectedPlan) {
          logger?.info('Subscription verified successfully', {
            requestId,
            attempts,
            elapsedMs: Date.now() - startTime,
            planType: subscription?.plan?.plan_type
          });

          return {
            verified: true,
            subscription: this.formatSubscription(subscription),
            error: null
          };
        }

        // Not yet activated, continue polling
        logger?.debug('Subscription not yet activated, retrying...', {
          requestId,
          attempt: attempts,
          status: subscription?.status,
          planType: subscription?.plan?.plan_type
        });

        // Wait before next attempt
        await this.sleep(intervalMs);

      } catch (error) {
        logger?.error('Subscription verification error', error, {
          requestId,
          attempt: attempts
        });

        // Wait before retry
        await this.sleep(intervalMs);
      }
    }

    // Max attempts reached
    logger?.warn('Subscription verification max attempts reached', {
      requestId,
      attempts,
      elapsedMs: Date.now() - startTime
    });

    return {
      verified: false,
      subscription: null,
      error: 'Subscription verification incomplete - please refresh the page in a moment'
    };
  },

  /**
   * Verify subscription is synced and active
   * Single check without polling
   * @returns {Promise<{verified: boolean, subscription: Object, error: string}>}
   */
  async verifySubscriptionSync() {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return {
          verified: false,
          subscription: null,
          error: 'User not authenticated'
        };
      }

      const { data: subscription, error: fetchError } = await supabase
        ?.from('user_subscriptions')
        ?.select(`
          *,
          plan:subscription_plans(*)
        `)
        ?.eq('user_id', user?.id)
        ?.maybeSingle();

      if (fetchError) {
        logger?.error('Failed to verify subscription sync', fetchError, { requestId });
        return {
          verified: false,
          subscription: null,
          error: 'Failed to fetch subscription'
        };
      }

      // If no subscription found, user doesn't have one
      if (!subscription) {
        return {
          verified: false,
          subscription: null,
          error: null // No error, just no subscription
        };
      }

      const isActive = subscription?.status === 'active';
      const isPremiumPlan = subscription?.plan?.plan_type !== 'basic' && 
                           subscription?.plan?.plan_type !== 'trial';

      return {
        verified: isActive && isPremiumPlan,
        subscription: this.formatSubscription(subscription),
        error: null
      };
    } catch (error) {
      logger?.error('Subscription sync verification error', error, { requestId });
      return {
        verified: false,
        subscription: null,
        error: 'Verification failed'
      };
    }
  },

  /**
   * Format subscription data from snake_case to camelCase
   * @param {Object} data - Raw subscription data
   * @returns {Object} Formatted subscription
   */
  formatSubscription(data) {
    if (!data) return null;

    return {
      id: data?.id,
      userId: data?.user_id,
      planId: data?.plan_id,
      status: data?.status,
      billingInterval: data?.billing_interval,
      currentPeriodStart: data?.current_period_start,
      currentPeriodEnd: data?.current_period_end,
      cancelAtPeriodEnd: data?.cancel_at_period_end,
      cancelledAt: data?.cancelled_at,
      trialStart: data?.trial_start,
      trialEnd: data?.trial_end,
      stripeCustomerId: data?.stripe_customer_id,
      stripeSubscriptionId: data?.stripe_subscription_id,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at,
      plan: {
        id: data?.plan?.id,
        planType: data?.plan?.plan_type,
        name: data?.plan?.name,
        description: data?.plan?.description,
        priceMonthly: data?.plan?.price_monthly,
        priceYearly: data?.plan?.price_yearly,
        features: data?.plan?.features,
        limits: data?.plan?.limits,
        isActive: data?.plan?.is_active
      }
    };
  },

  /**
   * Sleep utility for polling delays
   * @param {number} ms - Milliseconds to sleep
   * @returns {Promise<void>}
   */
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
};

export default subscriptionVerificationService;
